import { InfDetailsButtonV2 } from "@features/InfDetailsButtonV2";
import { ToastService } from "@shared/lib/services";
import { appendHttp } from "@shared/lib/utils/appendHttp";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Avatar, Button, Tag, Tooltip } from "antd";
import { Copy, User } from "lucide-react";
import { ReactNode } from "react";
import styles from "./styles.module.scss";

import { SocialMediaInfluencerRequest } from "@shared/lib/types/influencer";
import { getSocialMediaUrl } from "@shared/lib/utils/getSocialMediaUrl";

interface BriefCreatorColumnProps {
  influencerId?: string;
  influencerName?: string;
  username?: string;
  platform?: string;
  profilePicUrl?: string;
  link?: string;
  isCreatorDeclined?: boolean;
  isBrandDeclined?: boolean;
  creatorDeclinedReason?: string;
  brandDeclinedReason?: string;
  showStatusTag?: boolean;
  action?: ReactNode;
  hasNotification?: boolean;
  notificationText?: string;
}

export const BriefCreatorColumn = ({
  influencerId,
  influencerName,
  username,
  platform,
  profilePicUrl,
  link,
  isCreatorDeclined,
  isBrandDeclined,
  creatorDeclinedReason,
  brandDeclinedReason,
  showStatusTag = true,
  action,
  hasNotification,
  notificationText,
}: BriefCreatorColumnProps) => {
  const creatorDetailsReq = (
    influencerId
      ? { influencerId: influencerId }
      : {
          username: {
            socialMediaUsername: username || "",
            platform: platform || "",
          },
        }
  ) as SocialMediaInfluencerRequest;

  const creatorInfo = {
    name: influencerName,
    image: profilePicUrl,
    username: username,
  };

  return (
    <div className={styles.column}>
      <InfDetailsButtonV2
        creatorInfo={creatorInfo}
        socialMediaInfluencerReq={creatorDetailsReq}
      >
        <div className={styles.identity}>
          <div className={styles.avatarWrap}>
            <Avatar
              src={profilePicUrl ?? undefined}
              icon={profilePicUrl ? undefined : <User size={16}/>}
              alt={influencerName || "influencer"}
              className={styles.avatar}
            />
            {hasNotification ? (
              <span className={styles.notificationDot} />
            ) : null}
          </div>
          <div className={styles.meta}>
            <TextWithTooltip
              text={influencerName || ""}
              limit={10}
              textClassName={styles.influencerName}
            />

            <div className={styles.usernameRow}>
              <Button
                type="link"
                onClick={(e) => {
                  e.stopPropagation();
                  if (link) {
                    window.open(appendHttp(link || ""), "_blank");
                  } else {
                    window.open(
                      getSocialMediaUrl(platform, username),
                      "_blank"
                    );
                  }
                }}
                className={styles.usernameButton}
              >
                {getPlatformIcon(platform || "")}
                <TextWithTooltip text={username || ""} limit={10} />
              </Button>
              {username ? (
                <Tooltip title="Copy username">
                  <Copy
                    size={12}
                    className={styles.copyUsername}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(link || "");
                      ToastService.success("Link copied to clipboard");
                    }}
                  />
                </Tooltip>
              ) : null}
            </div>
            {notificationText ? (
              <span
                className={styles.notificationText}
                title={notificationText}
              >
                {notificationText}
              </span>
            ) : null}
            {(isCreatorDeclined || isBrandDeclined) && showStatusTag ? (
              <Tooltip
                title={
                  isCreatorDeclined
                    ? creatorDeclinedReason
                    : brandDeclinedReason
                }
              >
                <Tag color="red" className={styles.declinedTag}>
                  {isCreatorDeclined ? "Creator Declined" : "Brand Declined"}
                </Tag>
              </Tooltip>
            ) : null}
          </div>
        </div>
      </InfDetailsButtonV2>
      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  );
};
